import { Component, Input } from '@angular/core'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'

/**
 * Submit confirmation for the Corporate Actions Processing tab (PVAL-25952),
 * styled on `BulkTransactionsSaveConfirmationModalComponent`.
 *
 * The note about the Jobs panel is load-bearing, not decoration: a successful
 * POST only means the HeliosCommands were QUEUED. perseus-worker's cash
 * reconciliation, security eligibility, open lots and cost-basis checks run
 * asynchronously and surface via the existing job-failure toast + Jobs panel —
 * correlating them back to a row is explicitly out of scope for this cut.
 *
 * `activeModal.close(true)` is load-bearing too, and it is the ONE regression on
 * this tab's critical path that produces no red test unless it is pinned HERE:
 * `submit()` reads `await modalRef.result` as `confirmed`, so changing this to a
 * bare `close()` resolves `undefined`, `confirmed` goes falsy and EVERY Submit
 * becomes a silent no-op in the browser — while every submit case in
 * `corporate-actions-processing.component.spec.ts` stays green, because they all
 * stub `NgbModal` with `{ result: Promise.resolve(true) }` and never render this
 * template. `processing-submit-confirmation-modal.spec.ts` renders it for real and
 * asserts the confirm button resolves TRUTHY while cancel/close do not resolve at
 * all.
 */
@Component({
  selector: 'hs-corporate-actions-processing-submit-confirmation-modal',
  template: `
    <div class="modal-header" hsDraggable="modal-dialog">
      <h4 class="modal-title">Submit corporate action commands?</h4>
      <button
        nf-icon-button
        icon="close"
        class="btn-bare"
        ngbTooltip="Close"
        data-testid="processing-submit-confirmation-close"
        (click)="activeModal.dismiss()"
      >
        Close
      </button>
    </div>
    <div class="modal-body" hsDraggable="modal-dialog">
      <p data-testid="processing-submit-confirmation-summary">
        You are about to submit
        <b>{{ rowCount }}</b>
        {{ rowCount === 1 ? 'row' : 'rows' }} as
        <b>{{ commandLabel }}</b>
        commands.
      </p>
      <p data-testid="processing-submit-confirmation-async-note">
        Submitting queues these commands. Deeper validations (cash
        reconciliation, security eligibility, open lots, cost basis) run when
        the job executes and surface via the Jobs panel, not on the grid row.
      </p>
    </div>
    <div class="modal-footer" hsDraggable="modal-dialog">
      <button
        nf-button="cancel"
        data-testid="processing-submit-confirmation-cancel"
        (click)="activeModal.dismiss()"
      ></button>
      <button
        nf-button="submit"
        data-testid="processing-submit-confirmation-confirm"
        (click)="activeModal.close(true)"
      >
        Submit
      </button>
    </div>
  `,
})
export class ProcessingSubmitConfirmationModal {
  @Input() rowCount = 0
  @Input() commandLabel = ''

  constructor(public activeModal: NgbActiveModal) {}
}
